<!--
  Visual hierarchy (AGENTS.md §Visual hierarchy)
  Question: who do I still have money out with - contacts and other ledgers alike?
  Answer:   one row per target, its owed/repaid/forgiven figures.
  Context:  the tab strip.
  Controls: a per-row Forgive action.
-->
<script setup lang="ts">
import { ErrorState } from '@taler/ui'
import { computed, ref } from 'vue'
import { useI18n } from 'vue-i18n'
import { useDisplayCurrency } from '@/app/display-currency'
import { useSession } from '@/app/session'
import { ForgiveDialog, type ForgiveSubject } from '@/features/forgiveness'
import { canViewReporting, LentToTable, ReportFrame, useLentTo, type LentToItem } from '@/features/reporting'

const { t } = useI18n()
const session = useSession()
const { displayCurrency } = useDisplayCurrency()

const canQuery = computed(() => session.activeLedger.value !== undefined && canViewReporting(session.can))
const lentTo = useLentTo({
  ledgerId: () => session.activeLedger.value?.id,
  displayCurrency: () => displayCurrency.value,
  enabled: () => canQuery.value,
})

const forgiveTarget = ref<ForgiveSubject>()
function openForgive(row: LentToItem): void {
  forgiveTarget.value = { target: row.target, name: row.name, owed: [row.owed] }
}
function closeForgive(): void {
  forgiveTarget.value = undefined
}
</script>

<template>
  <ReportFrame :title="t('reporting.title')" :subtitle="t('reporting.lentTo.subtitle')">
    <p v-if="session.isLoading.value" role="status">{{ t('app.loading') }}</p>
    <p v-else-if="session.activeLedger.value === undefined" class="text-[color:var(--color-text-muted)]">
      {{ t('reporting.noLedger') }}
    </p>
    <p v-else-if="!canViewReporting(session.can)" class="text-[color:var(--color-text-muted)]">
      {{ t('reporting.noAccess') }}
    </p>
    <p v-else-if="lentTo.isLoading.value" role="status">{{ t('reporting.loading') }}</p>
    <ErrorState
      v-else-if="lentTo.error.value"
      :title="t('reporting.errorTitle')"
      :retry-label="t('reporting.retry')"
      :retrying="lentTo.retrying.value"
      @retry="lentTo.retry()"
    />
    <LentToTable v-else-if="lentTo.data.value" :rows="lentTo.data.value.items" @forgive="openForgive" />

    <ForgiveDialog
      v-if="session.activeLedger.value"
      :open="forgiveTarget !== undefined"
      :ledger-id="session.activeLedger.value.id"
      :subject="forgiveTarget"
      @close="closeForgive"
      @forgiven="closeForgive"
    />
  </ReportFrame>
</template>
